import { useEffect, useRef, useState } from "react";
import { adminListMedia, adminUploadMedia, mediaUrl } from "../lib/api";

/**
 * MediaPicker — visual replacement for URL text fields.
 *
 * Props:
 *   value           current image URL (may be relative `/api/media/...` or absolute)
 *   onChange(url)   called with the newly selected URL
 *   label           label shown above (e.g., "Cover image")
 *   testid          base for data-testid attributes (e.g., "cover" -> picker-cover, modal-cover)
 *
 * UX:
 *   - Shows a thumbnail preview of the current value (if any).
 *   - Buttons: Select from library / Upload new / Remove.
 *   - Library modal: grid of all media, click to pick.
 *   - Upload tab inside the modal: drag-drop + file picker, auto-selects the first uploaded item.
 */
const DEFAULT_FOCAL = { x: 50, y: 50, zoom: 1 };

export function MediaPicker({ value, onChange, label, testid = "media", required = false, focal, onFocalChange }) {
    const [open, setOpen] = useState(false);
    const showFocal = typeof onFocalChange === "function";
    const f = { ...DEFAULT_FOCAL, ...(focal || {}) };
    const setFocal = (patch) => onFocalChange({ ...f, ...patch });
    const handleClick = (e) => {
        if (!showFocal || !value) return;
        const r = e.currentTarget.getBoundingClientRect();
        const x = Math.round(((e.clientX - r.left) / r.width) * 100);
        const y = Math.round(((e.clientY - r.top) / r.height) * 100);
        setFocal({ x, y });
    };
    return (
        <div data-testid={`picker-${testid}`}>
            <p className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
                {label}
                {required && <span className="text-red-700"> *</span>}
            </p>
            <div className="flex items-start gap-4">
                <div
                    onClick={handleClick}
                    data-testid={`focal-${testid}`}
                    className={`w-32 h-20 bg-[#EFEBE4] border border-[#E5E1DB] overflow-hidden flex items-center justify-center relative ${
                        showFocal && value ? "cursor-crosshair" : ""
                    }`}
                    title={showFocal && value ? "Click to set focal point" : undefined}
                >
                    {value ? (
                        <img
                            src={mediaUrl(value)}
                            alt=""
                            className="w-full h-full object-cover pointer-events-none"
                            style={{ objectPosition: `${f.x}% ${f.y}%`, transform: `scale(${f.zoom})`, transformOrigin: `${f.x}% ${f.y}%` }}
                        />
                    ) : (
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8C7A6B]">No image</span>
                    )}
                    {showFocal && value && (
                        <span
                            aria-hidden="true"
                            className="absolute w-3 h-3 rounded-full border-2 border-white pointer-events-none"
                            style={{ left: `${f.x}%`, top: `${f.y}%`, transform: "translate(-50%, -50%)", boxShadow: "0 0 0 1px rgba(0,0,0,0.6)" }}
                        />
                    )}
                </div>
                <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid={`pick-${testid}`}
                        className="text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1 self-start"
                    >
                        {value ? "Replace from library" : "Select from media library"}
                    </button>
                    {value && (
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            data-testid={`remove-${testid}`}
                            className="text-[10px] uppercase tracking-[0.22em] text-red-700 self-start"
                        >
                            Remove
                        </button>
                    )}
                    {showFocal && value && (
                        <div className="mt-1 flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-[0.18em] text-[#8C7A6B]">
                            <label className="flex items-center gap-2">
                                Zoom
                                <input
                                    type="range"
                                    min="1"
                                    max="2.5"
                                    step="0.05"
                                    value={f.zoom}
                                    data-testid={`zoom-${testid}`}
                                    onChange={(e) => setFocal({ zoom: Number(e.target.value) })}
                                />
                                <span className="tabular-nums normal-case tracking-normal text-[#5C5855]">{f.zoom.toFixed(2)}×</span>
                            </label>
                            <span className="normal-case tracking-normal text-[#5C5855]">Focal {f.x}%, {f.y}%</span>
                            <button
                                type="button"
                                onClick={() => onFocalChange({ ...DEFAULT_FOCAL })}
                                data-testid={`reset-focal-${testid}`}
                                className="text-[#5C5855] hover:text-[#1A1A1A]"
                            >
                                Reset
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {open && (
                <MediaPickerModal
                    onClose={() => setOpen(false)}
                    onPick={(url) => { onChange(url); setOpen(false); }}
                    testid={testid}
                />
            )}
        </div>
    );
}

function MediaPickerModal({ onClose, onPick, testid }) {
    const [tab, setTab] = useState("library");
    const [media, setMedia] = useState(null);
    const [query, setQuery] = useState("");
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    useEffect(() => {
        const params = query ? { q: query } : {};
        adminListMedia(params).then(setMedia).catch((err) => setError(err?.message || "Load failed"));
    }, [query]);

    const upload = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        setProgress(0);
        setError("");
        try {
            const res = await adminUploadMedia(Array.from(files), (e) => {
                if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
            });
            if (res.uploaded && res.uploaded.length) {
                // Auto-pick the first newly uploaded item
                onPick(res.uploaded[0].url);
            }
        } catch (err) {
            setError(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <div
            role="dialog"
            data-testid={`modal-${testid}`}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-[#F9F8F6] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                <header className="px-6 py-4 border-b border-[#E5E1DB] flex items-center justify-between">
                    <h2 className="serif text-2xl tracking-tight">Media Library</h2>
                    <button onClick={onClose} data-testid={`modal-${testid}-close`} className="text-[11px] uppercase tracking-[0.22em] text-[#5C5855]">Close</button>
                </header>
                <nav className="px-6 pt-4 flex items-center gap-6 text-[11px] uppercase tracking-[0.22em]">
                    <button
                        onClick={() => setTab("library")}
                        data-testid={`tab-library-${testid}`}
                        className={`pb-2 ${tab === "library" ? "border-b border-[#1A1A1A] text-[#1A1A1A]" : "text-[#8C7A6B]"}`}
                    >Library</button>
                    <button
                        onClick={() => setTab("upload")}
                        data-testid={`tab-upload-${testid}`}
                        className={`pb-2 ${tab === "upload" ? "border-b border-[#1A1A1A] text-[#1A1A1A]" : "text-[#8C7A6B]"}`}
                    >Upload new</button>
                </nav>

                <div className="flex-1 overflow-y-auto p-6">
                    {error && <p className="text-red-700 text-sm mb-4">{error}</p>}
                    {tab === "library" ? (
                        <>
                            <input
                                data-testid={`modal-search-${testid}`}
                                type="search"
                                placeholder="Search by filename…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-2 text-sm w-72 mb-5"
                            />
                            {media == null ? (
                                <p className="asc-overline">Loading…</p>
                            ) : media.length === 0 ? (
                                <p className="text-[#5C5855]">No images yet — try the Upload new tab.</p>
                            ) : (
                                <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                                    {media.map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => onPick(m.url)}
                                            data-testid={`pick-item-${testid}-${m.id}`}
                                            className="group bg-white border border-[#E5E1DB] hover:border-[#1A1A1A] transition-colors text-left"
                                        >
                                            <div className="aspect-square bg-[#EFEBE4] overflow-hidden">
                                                <img src={mediaUrl(m.url)} alt={m.filename} loading="lazy" className="w-full h-full object-cover" />
                                            </div>
                                            <p className="px-2 py-2 text-[10px] text-[#1A1A1A] truncate" title={m.filename}>{m.filename}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div
                            data-testid={`modal-dropzone-${testid}`}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
                            onClick={() => fileRef.current?.click()}
                            className="border-2 border-dashed border-[#D5CFC5] bg-white p-12 text-center cursor-pointer hover:border-[#1A1A1A]"
                        >
                            <input
                                ref={fileRef}
                                data-testid={`modal-file-input-${testid}`}
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={(e) => upload(e.target.files)}
                            />
                            {uploading ? (
                                <>
                                    <p className="asc-overline">Uploading… {progress}%</p>
                                    <div className="mt-4 h-1 bg-[#E5E1DB] mx-auto max-w-md overflow-hidden">
                                        <div className="h-full bg-[#1A1A1A] transition-all" style={{ width: `${progress}%` }} />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="serif text-2xl tracking-tight text-[#1A1A1A]">Drop images here</p>
                                    <p className="mt-2 text-sm text-[#5C5855]">or click to choose files — first upload is auto-selected.</p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MediaPicker;
